import React from 'react';
import { stripMessageHtml, isMessageMentioningUser } from './mentionUtils';

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const getMessagePlainText = (message) => {
  if (!message?.content) return '';
  return stripMessageHtml(message.content);
};

export const getMessageCustomTagLabels = (message) => (
  (message?.customTags || [])
    .map((tag) => tag?.label?.trim())
    .filter(Boolean)
);

export const getMessageSearchableText = (message) => {
  const parts = [getMessagePlainText(message), ...getMessageCustomTagLabels(message)];
  if (message?.workflowTrackId?.trim()) {
    parts.push(message.workflowTrackId.trim());
  }
  return parts.join(' ').toLowerCase();
};

export const messagePlainTextIncludes = (message, query) => {
  if (!query?.trim()) return true;
  return getMessageSearchableText(message).includes(query.trim().toLowerCase());
};

export const messageMatchesInChatSearch = (message, { query, mentionsOnly, fromUserId, userId, username }) => {
  if (!message) return false;
  if (fromUserId && String(message.senderId) !== String(fromUserId)) {
    return false;
  }
  if (mentionsOnly && !isMessageMentioningUser(message, userId, username)) {
    return false;
  }
  if (query?.trim()) {
    return messagePlainTextIncludes(message, query);
  }
  return mentionsOnly || Boolean(fromUserId);
};

export const getChatSearchMatchIds = (messages, searchOptions) => {
  const { query, mentionsOnly, fromUserId } = searchOptions;
  if (!mentionsOnly && !query?.trim() && !fromUserId) {
    return [];
  }
  return (messages || [])
    .filter((message) => messageMatchesInChatSearch(message, searchOptions))
    .map((message) => message.id)
    .filter(Boolean);
};

export const filterMessagesForChatSearch = (messages, searchOptions) => {
  const { mentionsOnly, fromUserId } = searchOptions;
  if (!mentionsOnly && !fromUserId) {
    return messages || [];
  }
  return (messages || []).filter((message) => messageMatchesInChatSearch(message, searchOptions));
};

export const highlightSearchInHtml = (html, query) => {
  if (!query?.trim() || !html) return html;
  const escaped = escapeRegex(query.trim());
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = html.split(/(<[^>]+>)/g);
  return parts.map((part) => {
    if (!part || part.startsWith('<')) {
      return part;
    }
    return part.replace(
      regex,
      '<mark class="workloop-search-highlight">$1</mark>',
    );
  }).join('');
};

export const renderHighlightedSearchText = (text, query, keyPrefix = 'search') => {
  if (!text) return text;
  if (!query?.trim()) return text;

  const escaped = escapeRegex(query.trim());
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = String(text).split(regex);

  return parts.map((part, index) => {
    if (!part) return null;
    const isMatch = index % 2 === 1;
    if (isMatch) {
      return (
        <mark key={`${keyPrefix}-${index}`} className="workloop-search-highlight">
          {part}
        </mark>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${index}`}>{part}</React.Fragment>;
  });
};

export const wrapTextSegmentWithSearchHighlight = (renderSegment, query) => {
  if (!query?.trim()) {
    return renderSegment;
  }
  return (text) => {
    const rendered = renderSegment ? renderSegment(text) : text;
    if (typeof rendered === 'string') {
      return renderHighlightedSearchText(rendered, query, 'segment');
    }
    if (typeof text === 'string') {
      return renderHighlightedSearchText(text, query, 'segment');
    }
    return rendered;
  };
};
